import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "ncs_cart";

/**
 * Cart item shape:
 * { id, type, label, price, quantity, meta? }
 *   - id        : unique line identifier (stable for merging, e.g. "textile_canape_3p")
 *   - type      : "auto" | "textile" | "mecanique"
 *   - label     : human readable label shown in the cart and WhatsApp message
 *   - price     : unit price in euros (0 = "sur devis")
 *   - quantity  : integer >= 1
 *   - meta      : optional free-form details (date, créneau, options…)
 */

const CartContext = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Basic sanitation
    return parsed
      .filter((it) => it && typeof it.id === "string")
      .map((it) => ({
        id: it.id,
        type: it.type || "auto",
        label: String(it.label || "Prestation"),
        price: Number.isFinite(it.price) ? it.price : 0,
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        meta: it.meta || null,
      }));
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStorage);
  const [isOpen, setIsOpen] = useState(false);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* localStorage unavailable (private mode) — fail silently */
    }
  }, [items]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  /**
   * Add an item. If an item with the same id already exists, its quantity is
   * increased (handy for textile articles). Pass a unique id (e.g. with a
   * timestamp) when each entry must stay separate (e.g. auto configs).
   */
  const addItem = useCallback((item) => {
    if (!item || !item.id) return;
    const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === item.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [
        ...prev,
        {
          id: item.id,
          type: item.type || "auto",
          label: String(item.label || "Prestation"),
          price: Number.isFinite(item.price) ? item.price : 0,
          quantity: qty,
          meta: item.meta || null,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    const q = parseInt(quantity, 10) || 0;
    setItems((prev) => {
      if (q <= 0) return prev.filter((x) => x.id !== id);
      return prev.map((x) => (x.id === id ? { ...x, quantity: q } : x));
    });
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const total = useMemo(
    () => items.reduce((sum, it) => sum + it.price * it.quantity, 0),
    [items]
  );

  const count = useMemo(
    () => items.reduce((sum, it) => sum + it.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      count,
      isOpen,
      openCart,
      closeCart,
    }),
    [items, addItem, removeItem, updateQuantity, clearCart, total, count, isOpen, openCart, closeCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
